// Golf Start Journal — beginner-focused articles.
// Each post is hand-written and SEO-structured. Image URLs are stable
// Unsplash/Pexels assets used elsewhere in the app.
//
// content schema:
//   intro:      lead paragraph (renders large)
//   sections:   [{ heading, paragraphs: [string], list?: [string], pullquote?: string }]
//   takeaway:   closing summary paragraph

export const POSTS = [
  {
    slug: "beginner-golf-tips",
    title: "10 Beginner Golf Tips That Actually Lower Your Score",
    excerpt:
      "Forget the tour-pro YouTube rabbit hole. These ten fundamentals — grip, posture, tempo and course-management — are what experienced coaches drill first.",
    category: "Fundamentals",
    readTime: "7 min read",
    date: "Jan 14, 2026",
    image:
      "https://images.unsplash.com/photo-1535131749006-b7f58c99034b?auto=format&fit=crop&w=1600&q=80",
    meta: {
      title: "10 Beginner Golf Tips That Actually Lower Your Score · Golf Start Journal",
      description:
        "Coach-approved beginner golf tips covering grip, posture, tempo and course management. The fundamentals that drop your score the fastest.",
    },
    content: {
      intro:
        "Most new golfers swing thousands of balls before anyone tells them the small mechanical truths that separate a frustrating round from a fun one. Below are ten tips our coaches consistently teach first — none flashy, all proven.",
      sections: [
        {
          heading: "1. Build your grip before you build your swing",
          paragraphs: [
            "A neutral, slightly strong grip is the cheapest distance you'll ever buy. The Vs formed by your thumbs and forefingers should point at your right shoulder (for right-handers). Practice it at home, with no club — pressure level 4 out of 10.",
          ],
        },
        {
          heading: "2. Posture beats power",
          paragraphs: [
            "Tilt from the hips, not the waist. Knees soft, weight on the balls of your feet, spine long. If you feel your back rounding, reset. Athletic posture creates room for the club to swing on plane — no manipulation required.",
          ],
        },
        {
          heading: "3. Aim the clubface first, your body second",
          paragraphs: [
            "Most beginners aim with their feet. Pros aim the face first — that's the only part hitting the ball. Pick an intermediate target a foot in front of your ball, square the face to it, then build your stance around it.",
          ],
        },
        {
          heading: "4. Tempo is a 3-to-1 ratio",
          paragraphs: [
            "Three counts back, one count down. Slow your backswing, accelerate through the ball. Almost every beginner fault — slicing, topping, fat shots — stems from a backswing rushed faster than the downswing.",
          ],
        },
        {
          heading: "5. Quiet the lower body in the backswing",
          paragraphs: [
            "If your hips spin in the takeaway, the club gets pulled off plane. Imagine your lead hip stays over your lead heel until the club is at the top. It feels like turning your shoulders against a wall.",
          ],
        },
        {
          heading: "6. Pitch and putt is where scores drop",
          paragraphs: [
            "Half the shots in a round happen inside 50 yards. Spend half your practice time there. A clean bump-and-run beats a chunked flop every weekend.",
          ],
          pullquote:
            "If you only have 30 minutes to practice — putt for 15, chip for 10, full-swing for 5. Watch what happens in a month.",
        },
        {
          heading: "7. Learn one bail-out shot",
          paragraphs: [
            "Every beginner needs a low-risk go-to under pressure. A quiet 3/4 punch with a 7-iron, or a chip-and-run with a pitching wedge. Master one — it saves you double-bogeys for years.",
          ],
        },
        {
          heading: "8. Track three numbers, not your score",
          paragraphs: [
            "Fairways hit, greens hit, putts per round. These three tell you where to practice. Your score is a lagging indicator — these are leading.",
          ],
        },
        {
          heading: "9. Treat range time as practice, not warm-up",
          paragraphs: [
            "Don't pound a bucket the same direction with the same club. Pick a target, switch clubs, vary lies, simulate course shots. Quality reps beat volume.",
          ],
        },
        {
          heading: "10. Book a lesson before bad habits set",
          paragraphs: [
            "The single highest-ROI thing a new golfer can do is meet with a certified coach in the first month. Habits are cheap to fix early — expensive to fix after a thousand reps.",
          ],
        },
      ],
      takeaway:
        "None of these tips will make you a tour pro. All of them, applied together, will get you breaking 100 — then 90 — faster than buying any new club.",
    },
  },
  {
    slug: "what-clubs-beginners-need",
    title: "What Clubs Do Beginners Actually Need? A Practical Guide",
    excerpt:
      "You don't need 14 clubs to start playing golf. Here's the minimalist set most coaches recommend — and what to add as your game grows.",
    category: "Equipment",
    readTime: "6 min read",
    date: "Jan 22, 2026",
    image:
      "https://images.unsplash.com/photo-1593111774240-d529f12cf4bb?auto=format&fit=crop&w=1600&q=80",
    meta: {
      title: "Beginner Golf Clubs Guide · Which Clubs You Actually Need · Golf Start",
      description:
        "A coach-written guide to the clubs beginners actually need — driver, hybrid, irons, wedge, putter — plus a starter budget and what to upgrade first.",
    },
    content: {
      intro:
        "Walk into a pro shop and you'll be steered toward a 14-club set. Most beginners need closer to 7. The right starter bag is forgiving, repeatable and cheap enough to upgrade later.",
      sections: [
        {
          heading: "The seven clubs that cover 95% of beginner shots",
          paragraphs: [
            "Here's what we hand to a brand-new student before their first round:",
          ],
          list: [
            "Driver (10.5° or 12° loft) — for tee shots on par 4s and 5s.",
            "Hybrid (4H or 5H) — replaces the long irons that destroy confidence.",
            "7-iron — your range rat. Learn distance and tempo here first.",
            "9-iron — for approaches inside 130 yards.",
            "Pitching wedge — the chip-and-pitch all-rounder.",
            "Sand wedge (56°) — bunkers, soft chips and short flop shots.",
            "Putter — half your strokes will come from this club. Choose one that feels balanced in your hands.",
          ],
        },
        {
          heading: "Forgiveness beats workability",
          paragraphs: [
            "Cavity-back irons, oversized drivers, and high-MOI putters exist because they make off-center hits behave. As a beginner you'll mis-strike often — buy the technology that hides it.",
          ],
        },
        {
          heading: "Shaft flex matters more than brand",
          paragraphs: [
            "If your swing speed is below 85 mph (typical for new players), use Regular or Senior flex. Stiff shafts make beginners slice and lose distance. A $200 set with the right flex outperforms a $1,200 set with the wrong one.",
          ],
        },
        {
          heading: "Realistic starter budget",
          paragraphs: [
            "You can build a fantastic beginner bag for $400–600 used. Quality second-hand sets from Callaway, TaylorMade and Mizuno are abundant on resale marketplaces — most have years of life left. (We curate a small shelf on our /shop page.)",
          ],
          pullquote:
            "Spend the money you save on lessons, not gear. The coach will get you to 90. The new driver won't.",
        },
        {
          heading: "What to upgrade — and when",
          paragraphs: [
            "Around month six, replace your sand wedge with a fitted 56° (loft, bounce and grind matter once you're chipping consistently). After a year, get a putter fitting — length and head shape affect your stroke more than anyone expects.",
          ],
        },
      ],
      takeaway:
        "Seven forgiving clubs, the right flex, and a putter you actually like. That's a beginner bag. Everything else is marketing.",
    },
  },
  {
    slug: "driving-range-etiquette",
    title: "Driving Range Etiquette: The Unwritten Rules No One Tells Beginners",
    excerpt:
      "Where to stand, how fast to swing, what to do with your divots — and how to look like a regular on your first visit.",
    category: "Etiquette",
    readTime: "5 min read",
    date: "Jan 28, 2026",
    image:
      "https://images.unsplash.com/photo-1670254626993-aa642e1d1736?auto=format&fit=crop&w=1600&q=80",
    meta: {
      title: "Driving Range Etiquette for Beginners · Golf Start Journal",
      description:
        "A friendly guide to driving range etiquette — bay choice, pace, divots, safety and the unwritten rules that mark a respectful golfer.",
    },
    content: {
      intro:
        "The driving range is the most welcoming place in golf — but it has its own quiet language. Get these basics right and you'll fit in from day one.",
      sections: [
        {
          heading: "Choose your bay with care",
          paragraphs: [
            "If someone is mid-swing in the bay next to where you're walking, wait. Don't park your bag between two active golfers — the swinger needs peripheral calm. Look for a gap of one empty bay if you can.",
          ],
        },
        {
          heading: "Don't rake every ball",
          paragraphs: [
            "Pulling one ball at a time from the pile keeps your tempo honest. Raking five balls into a line and rapid-firing them turns practice into a race — and it's the fastest way to ingrain bad habits.",
          ],
        },
        {
          heading: "Mind the divots",
          paragraphs: [
            "If you're hitting off real grass, work backwards from the front of your stance. One row, one inch back per shot. It's neater for the staff and trains a consistent contact pattern.",
          ],
        },
        {
          heading: "Phones quiet, conversation quieter",
          paragraphs: [
            "Earbuds are fine. Speaker-phone calls are not. If you're talking with a friend, lower your voice once anyone within two bays is mid-routine.",
          ],
          pullquote:
            "Treat the range like a library where everyone happens to be holding a 7-iron. That's the energy.",
        },
        {
          heading: "Never cross the line of fire",
          paragraphs: [
            "Don't walk in front of an occupied bay. If you've left your towel out there, wait until the golfer steps back. It sounds obvious — happens every weekend.",
          ],
        },
        {
          heading: "Clean up your station",
          paragraphs: [
            "Return your bucket, replace the mat divot tool, and leave the bay tidier than you found it. Range staff notice. So do the regulars.",
          ],
        },
      ],
      takeaway:
        "Range etiquette is just empathy at 80 yards. Be quiet, be aware, and you'll feel at home faster than your swing improves.",
    },
  },
  {
    slug: "how-to-choose-a-golf-coach",
    title: "How to Choose a Golf Coach (And Avoid the Wrong One)",
    excerpt:
      "Certification, teaching style, communication, and the questions you must ask before your first lesson. A buyer's guide from inside the industry.",
    category: "Coaching",
    readTime: "8 min read",
    date: "Feb 03, 2026",
    image:
      "https://images.unsplash.com/photo-1758933067987-ac456b5d7266?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
    meta: {
      title: "How to Choose a Golf Coach · Beginner's Buyer Guide · Golf Start",
      description:
        "How to choose the right golf coach: certifications, teaching style, communication, and the five questions to ask before booking your first lesson.",
    },
    content: {
      intro:
        "The right coach can shave years off your learning curve. The wrong one will leave you stuck — and broke. Here's how to pick well, even on your first try.",
      sections: [
        {
          heading: "Look for credentials that mean something",
          paragraphs: [
            "PGA, USGTF, LPGA, and TPI are the gold standards. They require classroom hours, supervised teaching and ongoing education. A coach who says they're 'self-taught' may be brilliant — but you'll have to verify it through reviews and trial.",
          ],
        },
        {
          heading: "Style matters more than résumé",
          paragraphs: [
            "Some coaches are technical (planes, angles, video). Some are feel-based (athletic positions, metaphors). Some are course-strategy first. None is best — the best is the one whose language matches how you learn.",
          ],
        },
        {
          heading: "Communication is the actual product",
          paragraphs: [
            "A coach who can't explain a concept three different ways will leave you confused. On the first lesson, watch how they handle a question. Patience and clarity outrank tournament wins every time.",
          ],
        },
        {
          heading: "Questions to ask before booking",
          paragraphs: ["Send these in a message — a good coach welcomes them:"],
          list: [
            "What's your typical student? (You want overlap with your level and goals.)",
            "How do you structure a first lesson?",
            "Do you teach feels or mechanics?",
            "What does progress look like after 5 lessons?",
            "What's your cancellation and refund policy?",
          ],
          pullquote:
            "If a coach makes you feel embarrassed for being a beginner, leave. The right coach makes the first lesson feel like the best decision of your year.",
        },
        {
          heading: "Trial before you commit",
          paragraphs: [
            "Book one lesson. Two if you're unsure. Don't sign a package upfront — any reputable coach lets you sample first. Compatibility matters more than any review.",
          ],
        },
        {
          heading: "Where to find vetted coaches",
          paragraphs: [
            "Marketplaces like Golf Start exist precisely to remove the guesswork — every coach is credentialed, reviewed and matched by specialty (junior, short game, course management). Browse the roster and read the profiles before you message.",
          ],
        },
      ],
      takeaway:
        "Credentials, teaching style, clear communication, and a trial lesson. Get those four right and you've avoided 95% of bad coaching experiences.",
    },
  },
  {
    slug: "what-to-expect-during-golf-lessons",
    title: "What to Expect During Your First Golf Lessons",
    excerpt:
      "From the awkward first swing to the breakthrough on lesson three — a realistic preview of your first month of professional coaching.",
    category: "Coaching",
    readTime: "6 min read",
    date: "Feb 08, 2026",
    image:
      "https://images.unsplash.com/photo-1634140144664-f84083f1090a?fm=jpg&q=85&w=1600&auto=format&fit=crop",
    meta: {
      title: "What to Expect From Your First Golf Lessons · Golf Start Journal",
      description:
        "A realistic preview of your first month of golf lessons — what each session covers, how to prepare, and how progress actually feels.",
    },
    content: {
      intro:
        "Your first golf lesson can feel intimidating. It shouldn't. Here's exactly what a professional coach takes you through in the first hour, the first week, and the first month — so you arrive prepared, not anxious.",
      sections: [
        {
          heading: "Before the lesson — what to bring",
          paragraphs: ["You don't need much:"],
          list: [
            "A pair of athletic shoes (golf shoes optional for lesson 1).",
            "Loose, comfortable clothes you can rotate in.",
            "Water and a small towel.",
            "Your clubs if you have them. If not, every coach has loaners.",
          ],
        },
        {
          heading: "Lesson 1 — the assessment",
          paragraphs: [
            "The first 15 minutes are conversation: your goals, your background, any past injuries. Then the coach watches you hit a few balls — no instruction yet. They're collecting data. Expect to leave with one or two tiny fixes, not a swing overhaul.",
          ],
        },
        {
          heading: "Lesson 2 — fundamentals",
          paragraphs: [
            "Grip, posture, alignment, ball position. The boring stuff that makes the swing repeatable. You'll feel slightly worse here than in lesson 1 — that's normal. New movement patterns disrupt before they help.",
          ],
          pullquote:
            "Almost every student gets worse before they get better. That dip is where learning happens. Stay patient.",
        },
        {
          heading: "Lesson 3 — the breakthrough",
          paragraphs: [
            "By the third hour, the new patterns start to click. Most students hit their first 'effortless' drive somewhere around lesson 3 or 4. The feeling is unforgettable — and a little addictive.",
          ],
        },
        {
          heading: "Lesson 4–6 — the short game",
          paragraphs: [
            "A good coach shifts focus to chipping, pitching and putting around lesson 4. This is where actual scores drop. Expect indoor putting drills, distance ladders, and a few uncomfortable bunker reps.",
          ],
        },
        {
          heading: "Between lessons — what to practice",
          paragraphs: [
            "The coach gives 1–2 drills per lesson. Practice those drills only. Resist the temptation to scroll YouTube for more — you'll dilute the work. Quality reps on what your coach assigned beat any tip from the internet.",
          ],
        },
        {
          heading: "How fast you'll improve",
          paragraphs: [
            "A committed beginner with weekly lessons typically breaks 100 within 3–6 months and 90 within 12. Talent matters less than consistency. Show up, do the drills, trust the process.",
          ],
        },
      ],
      takeaway:
        "First lesson is an assessment, not a test. Come curious, listen more than you talk, and trust that the awkward middle is where the real progress hides.",
    },
  },
];

export function getPostBySlug(slug) {
  return POSTS.find((p) => p.slug === slug);
}
